import { useRewardConfig } from '../../hooks/useRewardConfig';
import { useCurrency } from '../../hooks/useCurrency';
import { Icon } from '../ui/Icon';

/* ── Gilded corner flourish (self-contained so the ladder works anywhere) ──── */
function Corners() {
  const flourish = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
      <path d="M2 2 L2 9 M2 2 L9 2" />
      <path d="M2 2 C 10 4, 12 8, 11 12" strokeOpacity="0.7" />
      <circle cx="6" cy="6" r="1.4" fill="currentColor" stroke="none" />
    </svg>
  );
  return (
    <>
      <span className="cart-corner tl">{flourish}</span>
      <span className="cart-corner tr">{flourish}</span>
      <span className="cart-corner bl">{flourish}</span>
      <span className="cart-corner br">{flourish}</span>
    </>
  );
}

/**
 * RewardsLadder — the storefront's free-shipping → free-gift → mystery-gift
 * progress bar. The single source of truth for the tier UI: both the cart and
 * checkout render this so they can never drift from each other.
 *
 * Thresholds, names, value and the gift on/off switch are admin-tunable via
 * /admin/rewards (persisted on SiteSettings, read through useRewardConfig), so
 * editing them in the admin updates this ladder everywhere on the next load.
 *
 * Props:
 *   total              cart subtotal in cents
 *   freeShippingCoupon true when an active coupon already grants free shipping
 *   className          optional extra classes on the wrapping <section>
 */
export default function RewardsLadder({ total = 0, freeShippingCoupon = false, className = '' }) {
  const rewards = useRewardConfig();
  const { fmt } = useCurrency();

  const FREE_SHIP_AT = rewards.freeShippingThresholdCents;
  const FREE_BALM_AT = rewards.freeGiftThresholdCents;
  const FREE_GIFT_AT = rewards.mysteryGiftThresholdCents;
  const balmName     = rewards.freeGiftName || 'free gift';
  const giftName     = rewards.mysteryGiftName || 'mystery gift';
  const giftsOn      = rewards.freeGiftEnabled;

  const freeShipUnlocked = total >= FREE_SHIP_AT || freeShippingCoupon;
  const balmUnlocked     = giftsOn && total >= FREE_BALM_AT;
  const giftUnlocked     = giftsOn && total >= FREE_GIFT_AT;

  // Ladder scales 0 → the top reward. With gifts on that's the mystery-gift tier
  // (ship + balm pips sit partway along); with gifts off it's just free shipping.
  const ladderTop  = giftsOn ? FREE_GIFT_AT : FREE_SHIP_AT;
  const ladderPct  = Math.min(100, (total / ladderTop) * 100);
  const shipPipPct = giftsOn ? (FREE_SHIP_AT / FREE_GIFT_AT) * 100 : 100;
  const balmPipPct = (FREE_BALM_AT / FREE_GIFT_AT) * 100;

  const ladderMessage = giftUnlocked
    ? `Everything unlocked — free shipping, a ${balmName} & a ${giftName} travel with your order.`
    : balmUnlocked
      ? `Free shipping + a ${balmName} secured ✦ add ${fmt(FREE_GIFT_AT - total)} more for a ${giftName}.`
      : freeShipUnlocked
        ? (giftsOn
            ? `Free shipping secured ✦ add ${fmt(FREE_BALM_AT - total)} more for a ${balmName}.`
            : 'Free shipping secured ✦ enjoy your order!')
        : `Add ${fmt(FREE_SHIP_AT - total)} more to unlock FREE shipping.`;

  return (
    <section className={`cart-frame p-6 ${className}`}>
      <Corners />
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl font-bold text-gray-900 dark:text-gray-100">
          Rewards Ladder
        </h2>
        <span className="cart-chip">
          <span className={giftUnlocked ? 'cart-sparkle' : ''}>✦</span>
          {giftUnlocked ? 'All unlocked' : balmUnlocked ? 'Balm unlocked' : freeShipUnlocked ? 'Ship unlocked' : 'Climbing'}
        </span>
      </div>

      <div className="relative pt-1 pb-7">
        <div className="cart-ladder-track">
          <div className="cart-ladder-fill" style={{ width: `${ladderPct}%` }} />
        </div>
        {/* checkpoints */}
        <div className={`cart-pip ${freeShipUnlocked ? 'is-hit' : ''}`} style={{ left: `${shipPipPct}%` }}
          title={`Free shipping at ${fmt(FREE_SHIP_AT)}`}>
          {freeShipUnlocked ? '✓' : <Icon name="truck" className="w-4 h-4" />}
        </div>
        {giftsOn && (
          <div className={`cart-pip ${balmUnlocked ? 'is-hit' : ''}`} style={{ left: `${balmPipPct}%` }}
            title={`${balmName} at ${fmt(FREE_BALM_AT)}`}>
            {balmUnlocked ? '✓' : <Icon name="sparkles" className="w-4 h-4" />}
          </div>
        )}
        {giftsOn && (
          <div className={`cart-pip ${giftUnlocked ? 'is-hit' : ''}`} style={{ left: '100%' }}
            title={`${giftName} at ${fmt(FREE_GIFT_AT)}`}>
            {giftUnlocked ? '✓' : <Icon name="gift" className="w-4 h-4" />}
          </div>
        )}
        {/* labels */}
        <span className="absolute top-9 text-[11px] font-semibold text-gray-500 dark:text-gray-400 -translate-x-1/2"
          style={{ left: `${shipPipPct}%` }}>{fmt(FREE_SHIP_AT)}</span>
        {giftsOn && (
          <span className="absolute top-9 text-[11px] font-semibold text-gray-500 dark:text-gray-400 -translate-x-1/2"
            style={{ left: `${balmPipPct}%` }}>{fmt(FREE_BALM_AT)}</span>
        )}
        {giftsOn && (
          <span className="absolute top-9 text-[11px] font-semibold text-gray-500 dark:text-gray-400 right-0">{fmt(FREE_GIFT_AT)}</span>
        )}
      </div>

      <p className="text-sm text-tea-700 dark:text-tea-300 font-medium text-center">
        {ladderMessage}
      </p>
    </section>
  );
}
